import { useEffect } from "react";

import { PayPalProvider, useEligibleMethods } from "@paypal/react-paypal-js/sdk-v6";
import { Elements } from "@stripe/react-stripe-js";
import { FieldValues } from "react-hook-form";

import { usePaymentErrorHandler } from "../PaymentElement/usePaymentErrorHandler";

import { StripePaymentComponentProps } from "./StripePaymentComponentProps";
import { useStripePaymentViewModel } from "./useStripePaymentViewModel";

import { StripeContext } from "@/common/contexts/StripeContext";

const PAYPAL_ENVIRONMENT = (process.env.NEXT_PUBLIC_PAYPAL_ENVIRONMENT || "sandbox") as "sandbox" | "production";
const PAYPAL_TEST_BUYER_COUNTRY = PAYPAL_ENVIRONMENT === "sandbox" ? "US" : undefined;

const VenmoEligibilityReporter = ({
  onEligibilityChange,
  currencyCode,
}: {
  onEligibilityChange: (eligible: boolean) => void;
  currencyCode: string;
}) => {
  const { eligiblePaymentMethods, isLoading } = useEligibleMethods({ payload: { currencyCode } }) as {
    eligiblePaymentMethods: { getDetails?: (method: string) => unknown } | null;
    isLoading: boolean;
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const venmoDetails: unknown = eligiblePaymentMethods?.getDetails?.("venmo");
    onEligibilityChange(Boolean(venmoDetails));
  }, [eligiblePaymentMethods, isLoading, onEligibilityChange]);

  return null;
};

/**
 * Form path: state comes from the parent (same `usePaymentErrorHandler` instance as
 * `PaymentErrorContext` for the step) and is passed as `paymentErrorState` so the
 * payment UI and submit/sibling code share one implementation without a nested
 * `PaymentErrorContext` consumer.
 */
function StripePaymentFormPath<T extends FieldValues>(props: StripePaymentComponentProps<T>) {
  const { getPaymentContent } = useStripePaymentViewModel(props);
  if (!props.paymentErrorState) {
    return null;
  }
  return getPaymentContent(props.paymentErrorState);
}

/**
 * Standalone / invoice: own `usePaymentErrorHandler` here; no `PaymentErrorContext` wrapper
 * (submit lives in the same tree as the payment content).
 */
function StripePaymentStandalonePath<T extends FieldValues>(props: StripePaymentComponentProps<T>) {
  const {
    getPaymentContent,
    stripeContextValue,
    elementsKey,
    stripeInstance,
    elementsOptions,
    isPayPalEnabled,
    paypalClientId,
    setIsVenmoSdkEligible,
    errorHandlerInput,
  } = useStripePaymentViewModel(props);
  const paymentErrorState = usePaymentErrorHandler(errorHandlerInput);

  const stripeTree = (
    <StripeContext.Provider value={stripeContextValue}>
      <Elements key={elementsKey} stripe={stripeInstance} options={elementsOptions}>
        {getPaymentContent(paymentErrorState)}
      </Elements>
    </StripeContext.Provider>
  );

  if (!paypalClientId) {
    return stripeTree;
  }

  return (
    <PayPalProvider
      clientId={paypalClientId}
      components={["paypal-payments", "venmo-payments"]}
      environment={PAYPAL_ENVIRONMENT}
      testBuyerCountry={PAYPAL_TEST_BUYER_COUNTRY}
      pageType="checkout"
    >
      {isPayPalEnabled && (
        <VenmoEligibilityReporter
          onEligibilityChange={setIsVenmoSdkEligible}
          currencyCode={(props.currency ?? "usd").toUpperCase()}
        />
      )}
      {stripeTree}
    </PayPalProvider>
  );
}

export function StripePaymentComponent<T extends FieldValues>(props: StripePaymentComponentProps<T>) {
  if (props.nestedInFormStripeTree) {
    return <StripePaymentFormPath {...props} />;
  }
  return <StripePaymentStandalonePath {...props} />;
}
